'use client';

/**
 * Structural rather than typed to one DTO: the generated draft and the quiz
 * already on the page are different objects, and anything not named here is
 * spread through untouched. Always laid on the quiz card, whose colour it wears.
 */
export interface EditableQuiz {
  title: string;
  questions: {
    text: string;
    options: { label: string; isCorrect: boolean }[];
  }[];
}

export function QuizFields<T extends EditableQuiz>({
  quiz,
  onChange,
}: {
  quiz: T;
  onChange: (next: T) => void;
}) {
  // A whole replacement question, not a patch: a patch merged onto a generic
  // element is a type TypeScript cannot prove is still that element.
  const replace = (index: number, question: T['questions'][number]) =>
    onChange({ ...quiz, questions: quiz.questions.map((q, i) => (i === index ? question : q)) });

  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Hook"
        placeholder="Your squat is wrong and question 3 proves it"
        value={quiz.title}
        onChange={(title) => onChange({ ...quiz, title })}
      />
      {quiz.questions.map((question, index) => {
        /* Found by `isCorrect`, never by position: options are stored in the
           writer's order and shuffled again on the way out. */
        const answer = (correct: boolean) =>
          question.options.find((option) => option.isCorrect === correct)?.label ?? '';
        const setAnswer = (correct: boolean, label: string) =>
          replace(index, {
            ...question,
            options: question.options.map((option) =>
              option.isCorrect === correct ? { ...option, label } : option
            ),
          });

        return (
          <div key={index} className="flex flex-col gap-1.5 rounded-xl bg-[rgba(0,0,0,0.25)] p-3">
            <Field
              label={`Question ${index + 1}`}
              value={question.text}
              onChange={(text) => replace(index, { ...question, text })}
            />
            <Field
              label="Right answer"
              value={answer(true)}
              onChange={(label) => setAnswer(true, label)}
            />
            <Field
              label="Wrong answer"
              value={answer(false)}
              onChange={(label) => setAnswer(false, label)}
            />
          </div>
        );
      })}
    </div>
  );
}

/** A labelled line of the quiz, on the card's own white paper. 16px so iOS never zooms. */
export function Field({
  label,
  placeholder,
  value,
  maxLength,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: string;
  /** The server's own cap, so a line is never refused by a limit the writer never saw. */
  maxLength?: number;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-badge font-bold uppercase tracking-wider text-white/60">{label}</span>
      <textarea
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={2}
        name={label}
        className="w-full resize-none rounded-xl bg-white/95 px-3 py-2 text-[16px] font-semibold leading-snug text-[#16110f] outline-none focus-visible:ring-2 focus-visible:ring-[#16110f]/50"
      />
    </label>
  );
}
